
import React, { useState, useEffect, useContext, createContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { updateSigninStats } from '@/utils/authSecurity';
import { clearImageCache } from '@/lib/imageCache';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isLoading: boolean;
  profileSelectionComplete: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileSelectionComplete, setProfileSelectionComplete] = useState(() => {
    return sessionStorage.getItem('profile-selection-shown') === 'true';
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let initialSessionResolved = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);

        // Resolve loading on first event
        if (!initialSessionResolved) {
          initialSessionResolved = true;
          setLoading(false);
        }
        if (event !== 'INITIAL_SESSION') {
          setLoading(false);
        }

        // Handle successful sign in - update stats
        if (event === 'SIGNED_IN' && session?.user?.id) {
          setTimeout(async () => {
            try {
              await updateSigninStats(session.user.id);
            } catch (error) {
              console.error('Failed to update signin stats:', error);
            }
          }, 0);

          clearImageCache();

          const profileSelectionShown = sessionStorage.getItem('profile-selection-shown');
          setProfileSelectionComplete(profileSelectionShown === 'true');
          
          if (location.pathname === '/auth') {
            navigate('/', { replace: true });
          }
        }

        if (event === 'PASSWORD_RECOVERY') {
          console.log('Password recovery initiated');
        }

        if (event === 'SIGNED_IN' && session?.user?.email_confirmed_at) {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('type') === 'signup') {
            window.history.replaceState({}, document.title, window.location.pathname);
            navigate('/', { replace: true });
          }
        }

        if (event === 'SIGNED_OUT') {
          localStorage.removeItem('profile-selection-shown');
          sessionStorage.removeItem('processing-payment');
          sessionStorage.removeItem('profile-selection-shown');
          clearImageCache();
          setProfileSelectionComplete(false);
          navigate('/', { replace: true });
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.id);
      if (!initialSessionResolved) {
        initialSessionResolved = true;
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
      
      if (session) {
        const profileSelectionShown = sessionStorage.getItem('profile-selection-shown');
        setProfileSelectionComplete(profileSelectionShown === 'true');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  // Watch for sessionStorage changes to update profile selection state
  useEffect(() => {
    const checkProfileSelection = () => {
      const profileSelectionShown = sessionStorage.getItem('profile-selection-shown');
      setProfileSelectionComplete(profileSelectionShown === 'true');
    };

    // Check on mount and when location changes
    checkProfileSelection();
    
    // Listen for storage events (when sessionStorage is updated)
    window.addEventListener('storage', checkProfileSelection);
    
    return () => {
      window.removeEventListener('storage', checkProfileSelection);
    };
  }, [location.pathname]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      isLoading: loading, 
      profileSelectionComplete 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
